/**
 * 资源加载器类
 * 用于加载外部脚本或样式表，支持多个备用 URL 和全局单例模式
 */
export class ResourceLoader {
  private loaded = false;
  private loading = false;
  private loadPromise: Promise<void> | null = null;
  private checkInterval = 100; // 轮询间隔（毫秒）

  /**
   * @param urls - 资源 URL 列表（主 URL 和备用 URL）
   * @param type - 资源类型：'script' 或 'style'
   */
  constructor(private urls: string[], private type: 'script' | 'style' = 'script') {}

  /**
   * 加载资源
   * @returns Promise，资源加载完成时 resolve
   */
  async load(): Promise<void> {
    // 如果已经加载完成，直接返回
    if (this.loaded) {
      return Promise.resolve();
    }

    // 如果正在加载，返回现有的 Promise（防止并发问题）
    if (this.loading && this.loadPromise) {
      return this.loadPromise;
    }

    // 开始加载（设置状态）
    this.loading = true;
    this.loadPromise = this.loadWithFallback();

    try {
      await this.loadPromise;
      this.loaded = true;
    } catch (err) {
      // 加载失败，重置状态以便下次重试
      this.loaded = false;
      throw err;
    } finally {
      this.loading = false;
    }

    return this.loadPromise;
  }

  /**
   * 尝试从 URL 列表依次加载资源
   */
  private async loadWithFallback(): Promise<void> {
    for (let i = 0; i < this.urls.length; i++) {
      const url = this.urls[i];
      if (!url) continue;

      try {
        await this.loadSingleResource(url);
        return; // 加载成功，退出
      } catch (err) {
        console.warn(`Failed to load ${this.type} from ${url}`, err);
        // 如果是最后一个 URL，抛出错误
        if (i === this.urls.length - 1) {
          throw new Error(`Failed to load ${this.type} from all URLs`);
        }
        // 否则继续尝试下一个 URL
      }
    }
  }

  /**
   * 加载单个资源
   */
  private loadSingleResource(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.type === 'script') {
        this.loadScript(url, resolve, reject);
      } else {
        this.loadStyle(url, resolve, reject);
      }
    });
  }

  /**
   * 加载脚本
   */
  private loadScript(url: string, resolve: () => void, reject: (error: Error) => void): void {
    const script = document.createElement('script');
    script.src = url;
    script.async = true;

    script.onload = () => {
      // 使用 requestAnimationFrame 确保脚本执行完成并且 DOM 有机会更新
      requestAnimationFrame(() => {
        resolve();
      });
    };
    script.onerror = () => reject(new Error(`Failed to load script: ${url}`));

    document.head.append(script);
  }

  /**
   * 加载样式表
   */
  private loadStyle(url: string, resolve: () => void, reject: (error: Error) => void): void {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;

    link.onload = () => resolve();
    link.onerror = () => reject(new Error(`Failed to load style: ${url}`));

    document.head.append(link);
  }

  /**
   * 检查资源是否已加载
   */
  isLoaded(): boolean {
    return this.loaded;
  }

  /**
   * 检查资源是否正在加载
   */
  isLoading(): boolean {
    return this.loading;
  }

  /**
   * 等待资源加载完成（轮询方式）
   * @param checkFn - 检查函数，返回 true 表示资源已就绪
   * @param timeout - 超时时间（毫秒），默认 10000
   */
  async waitUntil(checkFn: () => boolean, timeout = 10000): Promise<void> {
    const startTime = Date.now();

    return new Promise((resolve, reject) => {
      const check = () => {
        if (checkFn()) {
          resolve();
        } else if (Date.now() - startTime > timeout) {
          reject(new Error('Timeout waiting for resource'));
        } else {
          setTimeout(check, this.checkInterval);
        }
      };
      check();
    });
  }
}
